import { useTranslation } from 'react-i18next';
/**
 * PublicRecordView — what a phone shows after scanning a QR sticker.
 *
 * Unauthenticated: the token in the URL is the only key. Everything
 * shown here has already been redacted server-side (no money, no
 * people's names, no suppliers). Slugs keep the printed URL short:
 *   /p/a/<token>  asset   /p/p/<token>  project
 *   /p/m/<token>  machine  /p/c/<token>  checklist
 */
import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronRight, Download, FileText, Loader2, Lock, ShieldCheck,
} from 'lucide-react';
import PhotoLightbox from './PhotoLightbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUGS = { a: 'asset', p: 'project', m: 'machine', c: 'checklist' };

const STATUS_TONE = (s = '') => {
  const t = s.toLowerCase();
  if (t.includes('signed') || t.includes('commissioned') || t.includes('handed')) return 'bg-green-100 text-green-800';
  if (t.includes('conditional') || t.includes('installed')) return 'bg-amber-100 text-amber-800';
  if (t.includes('decommissioned') || t.includes('fail')) return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-700';
};
const ITEM_TONE = { Pass: 'text-green-700', Fail: 'text-red-600', 'N/A': 'text-slate-400' };

export default function PublicRecordView() {
  const { t } = useTranslation();
  const { slug, token } = useParams();
  const kind = SLUGS[slug] || slug;
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    axios.get(`${API}/public/record/${slug}/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || 'This code could not be read'));
  }, [slug, token]);

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="max-w-sm text-center bg-white rounded-lg shadow p-8" data-testid="public-record-error">
        <Lock className="w-8 h-8 mx-auto text-slate-400" />
        <h1 className="text-lg font-semibold text-slate-800 mt-3">{t('auto.misc.notAvailable')}</h1>
        <p className="text-sm text-slate-600 mt-2">{error}</p>
        <Link to="/" className="inline-block mt-5 text-sm text-[#AA0000] hover:underline">{t('auto.misc.goToTheApp')}</Link>
      </div>
    </div>
  );
  if (!data) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}${data.pdf_url}`;

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="public-record-view">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-2xl mx-auto px-4 py-4 sm:px-6">
          {data.company_logo
            ? <img src={data.company_logo} alt={data.company_name} className="h-8 w-auto object-contain" />
            : <span className="font-semibold text-slate-800">{data.company_name}</span>}
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="bg-white rounded-b-lg shadow-sm px-4 py-5 sm:px-6">
          <p className="text-xs uppercase tracking-wider text-[#AA0000] font-semibold">{data.code}</p>
          <h1 className="text-2xl font-semibold text-slate-900 mt-0.5 break-words" data-testid="public-record-title">
            {data.title}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2">
            {data.subtitle && <span className="text-sm text-slate-500 break-words">{data.subtitle}</span>}
            {data.status && (
              <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_TONE(data.status)}`}
                data-testid="public-record-status">{data.status}</span>
            )}
          </div>
          {data.description && (
            <p className="text-sm text-slate-700 mt-3 bg-slate-50 rounded p-3 break-words">{data.description}</p>
          )}
          <div className="flex flex-wrap gap-2 mt-4">
            <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="public-record-pdf"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md bg-[#AA0000] text-white hover:bg-[#880000] transition-colors">
              <Download className="w-4 h-4" /> {t('auto.misc.downloadPdf')}
            </a>
            <Link to={data.staff_path || '/'} data-testid="public-record-login"
              className="inline-flex items-center gap-2 text-sm px-3 py-2 rounded-md border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 transition-colors">
              {data.routine_check ? t('auto.misc.signInToComplete') : t('auto.misc.staffSignIn')} <ChevronRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {data.routine_check && (
          <section className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden" data-testid="public-routine-check">
            <h2 className="text-sm font-semibold text-slate-700 px-4 py-2.5 bg-slate-50 border-b">
              {t('auto.misc.routineCheck')}
            </h2>
            <div className="px-4 py-3">
              <p className="text-sm text-slate-800 break-words">{data.routine_check.name}</p>
              {data.routine_check.last_completed ? (
                <p className="text-xs text-slate-500 mt-1">
                  Last completed {data.routine_check.last_completed} · {data.routine_check.last_status}
                  {data.routine_check.last_url && (
                    <Link to={data.routine_check.last_url} className="text-[#AA0000] hover:underline ml-2"
                      data-testid="public-routine-last">{t('auto.misc.view')}</Link>
                  )}
                </p>
              ) : (
                <p className="text-xs text-slate-500 mt-1">{t('auto.misc.noCompletedCheckOnRecordYet')}</p>
              )}
            </div>
          </section>
        )}

        {(data.photos || []).length > 0 && (
          <div className={`mt-4 ${data.photos.length > 1 ? 'grid grid-cols-2 sm:grid-cols-3 gap-2' : ''}`}>
            {data.photos.map((src, i) => (
              <button key={src} type="button" onClick={() => setLightboxIndex(i)}
                className={`block cursor-zoom-in rounded-lg transition-opacity hover:opacity-90 ${
                  data.photos.length > 1 ? 'w-full' : 'mx-auto max-w-full'}`}
                aria-label={t('auto.misc.viewPhotoFullScreen')}
                data-testid={`public-record-photo-btn-${i}`}>
                <img src={`${process.env.REACT_APP_BACKEND_URL}${src}`} alt={data.title}
                  data-testid="public-record-photo"
                  className={`rounded-lg border border-slate-200 bg-white object-contain ${
                    data.photos.length > 1
                      ? 'w-full h-28'
                      : 'mx-auto block w-auto max-w-full max-h-[28rem]'}`} />
              </button>
            ))}
          </div>
        )}

        {lightboxIndex !== null && (data.photos || []).length > 0 && (
          <PhotoLightbox
            photos={data.photos.map((s) => `${process.env.REACT_APP_BACKEND_URL}${s}`)}
            index={lightboxIndex}
            onIndexChange={setLightboxIndex}
            onClose={() => setLightboxIndex(null)}
            alt={data.title}
          />
        )}

        {(data.sections || []).map((sec) => (
          <section key={sec.title} className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden"
            data-testid={`public-section-${sec.title.toLowerCase().replace(/\s+/g, '-')}`}>
            <h2 className="text-sm font-semibold text-slate-700 px-4 py-2.5 bg-slate-50 border-b">{sec.title}</h2>
            <dl className="divide-y divide-slate-100">
              {sec.rows.map((r, i) => (
                <div key={`${r.label}-${i}`} className="flex flex-col sm:flex-row sm:gap-4 px-4 py-2.5">
                  <dt className="text-xs text-slate-500 sm:w-48 sm:shrink-0">{r.label}</dt>
                  <dd className="text-sm text-slate-800 break-words min-w-0">{r.value}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}

        {(data.results || []).map((sec) => (
          <section key={sec.title} className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden">
            <h2 className="text-sm font-semibold text-slate-700 px-4 py-2.5 bg-slate-50 border-b">
              {sec.title}
              {sec.reference && <span className="font-normal text-slate-400 text-xs ml-2">{sec.reference}</span>}
            </h2>
            <ul className="divide-y divide-slate-100">
              {sec.items.map((it, i) => (
                <li key={i} className="px-4 py-2.5 flex items-start gap-3">
                  <span className="text-sm text-slate-800 min-w-0 flex-1 break-words">{it.label}</span>
                  <span className="text-right shrink-0">
                    <span className={`text-xs font-semibold ${ITEM_TONE[it.status] || 'text-slate-400'}`}>
                      {it.status || '—'}
                    </span>
                    {it.value && <span className="block text-xs text-slate-500">{it.value}</span>}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}

        {(data.sign_offs || []).length > 0 && (
          <section className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden">
            <h2 className="text-sm font-semibold text-slate-700 px-4 py-2.5 bg-slate-50 border-b">{t('auto.misc.signOff')}</h2>
            <ul className="divide-y divide-slate-100">
              {data.sign_offs.map((s) => (
                <li key={s.role} className="px-4 py-2.5 flex items-center justify-between gap-3">
                  <span className="text-sm text-slate-700">{s.role}</span>
                  <span className={`text-xs ${s.signed ? 'text-green-700' : 'text-slate-400'}`}>
                    {s.signed ? <><ShieldCheck className="w-3.5 h-3.5 inline mr-1" />Signed{s.date ? ` · ${s.date}` : ''}</> : 'Not signed'}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(data.specs || []).length > 0 && (
          <section className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden" data-testid="public-specs">
            <h2 className="text-sm font-semibold text-slate-700 px-4 py-2.5 bg-slate-50 border-b">
              {t('auto.misc.specification')}
            </h2>
            <ul className="divide-y divide-slate-100">
              {data.specs.map((s) => (
                <li key={s.label} className="px-4 py-2 flex items-start justify-between gap-3">
                  <span className="text-sm text-slate-600 min-w-0 break-words">{s.label}</span>
                  <span className="text-sm font-semibold text-slate-800 text-right shrink-0">{s.value}</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {(data.checklists || []).length > 0 && (
          <section className="bg-white rounded-lg shadow-sm mt-4 overflow-hidden" data-testid="public-checklists">
            <h2 className="text-sm font-semibold text-slate-700 px-4 py-2.5 bg-slate-50 border-b">
              {t('auto.misc.checklistsOnRecord')}
            </h2>
            <ul className="divide-y divide-slate-100">
              {data.checklists.map((c) => (
                <li key={c.url} className="px-4 py-3 flex items-center justify-between gap-3">
                  <Link to={c.url} className="min-w-0 group" data-testid={`public-checklist-${c.number}`}>
                    <p className="text-sm text-slate-800 group-hover:text-[#AA0000] transition-colors break-words">
                      {c.name}
                    </p>
                    <p className="text-xs text-slate-500">
                      {[c.number, c.status, c.date].filter(Boolean).join(' · ')}
                    </p>
                  </Link>
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${c.pdf}`} target="_blank" rel="noreferrer"
                    className="shrink-0 text-slate-400 hover:text-[#AA0000] transition-colors" title={t('auto.misc.pdf')}>
                    <FileText className="w-4 h-4" />
                  </a>
                </li>
              ))}
            </ul>
          </section>
        )}

        <p className="text-[11px] text-slate-400 text-center mt-6 px-4">
          Public {kind} record · generated {data.generated_at} · personal and commercial data is not shown.
          Sign in for the full record.
        </p>
      </main>
    </div>
  );
}
